import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { apiRequest, getUserDetails, setUserDetails } from "../../../services/apiRequest";
import { requestType } from "../../../services/requests";
import { addToErrorMessage } from "../../../features/errorMessage/errorMessageSlice";
import { addToLoader } from "../../../features/loader/loaderSlice";

import FormInput from "../../reusableComponents/FormInput/FormInput";
import Button from "../../reusableComponents/Button/Button";

import Logo from "../../../assets/svgs/buildHolidayLogoBlue.svg?react";

import { getCurrentTranslations } from "../../../utilities/helper";
import Typography from "../../reusableComponents/Typography/Typography";
import {
    handleStateCountrySelector,
    updateCountryCode,
} from "../../../features/countrySelector/countrySelectorSlice";
import translationResources from "../../../utilities/multiLanguage";
import FrontLayout from "../../reusableComponents/FrontLayout/FrontLayout";
import * as Sentry from "@sentry/react";

function LoginNew() {
    // Handle Translations
    const [translations, setTranslations] = useState({});
    useEffect(() => {
        setTranslations(translationResources["login"][window.currentLanguage]);
    }, []);
    //End ./ Handle Translations

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const passwordFieldRef = useRef(null);
    const [form, setForm] = useState({
        email: "",
        password: "",
    });
    const [firstLoading, setFirstLoading] = useState(true);
    const [staySignedIn, setStaySignedIn] = useState(false);

    const handleShowPassword = () => {
        if (passwordFieldRef.current.type === "text") {
            passwordFieldRef.current.type = "password";
        } else {
            passwordFieldRef.current.type = "text";
        }
    };

    const handleInput = (type, value) => {
        setForm({ ...form, [type]: value });
    };

    const handleSubmit = async () => {
        dispatch(addToLoader(true));
        // A rejected request (network failure, timeout) used to skip the
        // addToLoader(false) dispatch and strand the user behind the
        // full-screen loader overlay with no visible error.
        try {
            const response = await apiRequest({
                payload: {
                    data: { ...form, user_type: null },
                },
                requestType: requestType.authUser,
            });

            if (response.data.status === 1) {
                dispatch(addToErrorMessage(response.data.error_message));
            } else if (!setUserDetails(response.data.data)) {
                dispatch(addToErrorMessage("Login failed. Please try again."));
            } else {
                navigate("/home");
            }
        } catch (error) {
            Sentry.captureException(error);
            dispatch(
                addToErrorMessage("Login failed. Please check your connection and try again.")
            );
        } finally {
            dispatch(addToLoader(false));
        }
    };

    // useEffect(() => {
    //     document.title = "Login";
    // }, []);

    function handleSignUpButton() {
        dispatch(updateCountryCode(""));
        dispatch(handleStateCountrySelector());
        navigate("/country-selector");
        //navigate("/register"); //this is moved to 'country selector' popup
    }

    useEffect(() => {
        if (getUserDetails()) {
            navigate("/home");
        } else {
            setFirstLoading(false);
        }

        dispatch(updateCountryCode(""));
    }, []);

    if (firstLoading) {
        return;
    }

    return (
        <>
            <FrontLayout
                children={
                    <>
                        <Logo className="login_headerLogo mx-auto mt-4 mb-[1.875rem]" />
                        <p className="font-darwinProRegular text-base text-rebrandText mx-auto text-center mb-[1.875rem]">
                            {getCurrentTranslations("login", "textWelcomeTo")}{" "}
                            <span className="font-darwinProBold">
                                {getCurrentTranslations("login", "textGetStarted")}
                            </span>
                        </p>
                        <div className="w-full mx-auto">
                            <FormInput
                                type="text"
                                placeholder={getCurrentTranslations(
                                    "login",
                                    "placeholderEmailAddress"
                                )}
                                className="mb-[1rem] rounded-none"
                                color="gray"
                                value={form.email}
                                onChange={(e) => handleInput("email", e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleSubmit();
                                    }
                                }}
                            />
                            <div className="relative w-full">
                                <FormInput
                                    className="rounded-none"
                                    type="password"
                                    placeholder={getCurrentTranslations(
                                        "login",
                                        "placeholderPassword"
                                    )}
                                    ref={passwordFieldRef}
                                    value={form.password}
                                    color="gray"
                                    onChange={(e) => handleInput("password", e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSubmit();
                                        }
                                    }}
                                />
                                <span
                                    className="absolute cursor-pointer top-5 right-4 text-[#a6a6bd] text-[12px]"
                                    onClick={() => handleShowPassword()}
                                >
                                    {getCurrentTranslations("login", "textShow")}
                                </span>
                            </div>
                            <div className="relative w-full mt-4">
                                <div className="flex items-center justify-between">
                                    {/* <label className="flex items-center text-base cursor-pointer text-rebrandText font-darwinProRegular">
                                        <input
                                            type="checkbox"
                                            className="w-6 h-6 mr-2" // Added width and height to make it 24px
                                            checked={staySignedIn}
                                            onChange={() => setStaySignedIn(!staySignedIn)}
                                        />
                                        {getCurrentTranslations("login", "textStaySignedIn")}
                                    </label> */}
                                    <a
                                        href="/reset-password"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate("/reset-password");
                                        }}
                                        className="ml-auto text-base cursor-pointer text-rebrandRcBlueTwo font-darwinProRegular"
                                        rel="noreferrer"
                                    >
                                        {getCurrentTranslations("login", "textForgotPassword")}
                                    </a>
                                </div>
                            </div>

                            <div className="w-full mt-10">
                                <div className="w-full">
                                    <Button
                                        text={getCurrentTranslations("login", "buttonLogIn")}
                                        type={10}
                                        className="w-full mb-5"
                                        onClick={() => handleSubmit()}
                                    />
                                </div>
                                <div className="w-full mt-10">
                                    <Typography type="body2" className="mb-4 text-rebrandText">
                                        {getCurrentTranslations("login", "textHaventRegister")}
                                    </Typography>
                                    <Button
                                        text={getCurrentTranslations("login", "buttonSignUp")}
                                        className="w-full mb-5"
                                        type={9}
                                        onClick={() => handleSignUpButton()}
                                    />
                                    <div className="flex items-center justify-center w-full gap-4">
                                        {/* <a
                                    href="/"
                                    target="_blank"
                                    className="text-base underline cursor-pointer text-rebrandText font-darwinProRegular"
                                    rel="noreferrer"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onOpenLinkInNewTab("/terms-and-conditions");
                                    }}
                                >
                                    Terms & Conditions
                                </a> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
            />
        </>
    );
}

export default LoginNew;
